import { useState } from "react";
import { useAllDiscounts } from "http-hooks/discount/useDiscount";
import { Badge } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import Select from "components/ui/Select";
import { CheckIcon, XMarkIcon, MinusIcon, PlusIcon } from "@heroicons/react/20/solid";
import Modal from "components/ui/modals";
import SetDiscount from "components/discount/SetDiscount";
import DeleteDiscount from "components/discount/DeleteDiscount";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  EditAction,
  DeleteAction,
} from "components/ui/MasterPage";

const categoryOptions = [
  { value: "", label: "All Categories" },
  { value: "P", label: "Purchase" },
  { value: "S", label: "Sales" },
];

const typeOptions = [
  { value: "", label: "All Types" },
  { value: "A", label: "Annual" },
  { value: "B", label: "Before-tax Bill" },
  { value: "C", label: "After-tax Bill" },
];

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Non-Active" },
];

const initialFilter = {
  name: "",
  type: "",
  tp: "",
  ag_status: "",
};

const columns = [
  { key: "code", header: "Order" },
  { key: "name", header: "Name" },
  { key: "shortname", header: "Short Name" },
  {
    key: "tp",
    header: "Type",
    render: (d) => (
      <Badge
        variant={d.tp === "A" ? "info" : d.tp === "B" ? "primary" : "warning"}
      >
        {d.tp === "A" ? "Annual" : d.tp === "B" ? "Before-tax Bill" : "After-tax Bill"}
      </Badge>
    ),
  },
  {
    key: "type",
    header: "Category",
    render: (d) => (
      <Badge variant={d.type === "P" ? "warning" : "success"}>
        {d.type === "P" ? "Purchase" : "Sales"}
      </Badge>
    ),
  },
  {
    key: "is_addition",
    header: "+/−",
    render: (d) =>
      d.is_addition === 1 ? (
        <PlusIcon className="h-5 w-5 text-blue-500" aria-hidden="true" title="Add (Charge)" />
      ) : (
        <MinusIcon className="h-5 w-5 text-orange-500" aria-hidden="true" title="Less (Discount)" />
      ),
  },
  {
    key: "ag_status",
    header: "Status",
    render: (d) =>
      d.ag_status === 1 ? (
        <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
      ) : (
        <XMarkIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
      ),
  },
  { key: "actions", isActions: true },
];

export default function DiscountMaster() {
  const [filter, setFilter] = useState(initialFilter);
  const [setModalOpen, setSetModalOpen] = useState(false);
  const [deleteDiscountId, setDeleteDiscountId] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(null);

  const { data: discounts = [], isLoading } = useAllDiscounts(filter);

  const handleSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, name: searchTerm }));
  };

  const handleCategoryChange = (option) => {
    setFilter((prev) => ({ ...prev, type: option?.value || "" }));
  };

  const handleTypeChange = (option) => {
    setFilter((prev) => ({ ...prev, tp: option?.value || "" }));
  };

  const handleStatusChange = (option) => {
    setFilter((prev) => ({ ...prev, ag_status: option?.value || "" }));
  };

  const openSetModal = (discount = null) => {
    setEditingDiscount(discount);
    setSetModalOpen(true);
  };

  const closeSetModal = () => {
    setEditingDiscount(null);
    setSetModalOpen(false);
  };

  const closeDeleteModal = () => {
    setDeleteDiscountId(null);
  };

  return (
    <MasterPage
      title="Discount Masters"
      headerActions={
        <AddButton onClick={() => openSetModal()}>Add Discount</AddButton>
      }
      filters={
        <>
          <FilterRow>
            <FilterCell>
              <InputSearch
                onSearch={handleSearch}
                label="Search Discounts"
                placeholder="Discount name..."
              />
            </FilterCell>
            <FilterCell>
              <Select
                value={categoryOptions.find((o) => o.value === filter.type)}
                onChange={handleCategoryChange}
                options={categoryOptions}
                label="Category"
                placeholder="Select category"
                isClearable={true}
                isSearchable={false}
              />
            </FilterCell>
            <FilterCell>
              <Select
                value={typeOptions.find((o) => o.value === filter.tp)}
                onChange={handleTypeChange}
                options={typeOptions}
                label="Type"
                placeholder="Select type"
                isClearable={true}
                isSearchable={false}
              />
            </FilterCell>
            <FilterCell>
              <Select
                value={statusOptions.find((o) => o.value === filter.ag_status)}
                onChange={handleStatusChange}
                options={statusOptions}
                label="Status"
                placeholder="Select status"
                isClearable={true}
                isSearchable={false}
              />
            </FilterCell>
          </FilterRow>
          <FilterRow>
            <ClearFiltersButton onClick={() => setFilter(initialFilter)} />
          </FilterRow>
        </>
      }
      columns={columns}
      data={discounts}
      isLoading={isLoading}
      rowKey="id"
      renderActions={(discount) => (
        <>
          <EditAction onClick={() => openSetModal(discount)} variant="secondary" />
          <DeleteAction onClick={() => setDeleteDiscountId(discount.id)} />
        </>
      )}
      extraModals={
        <>
          <SetDiscount open={setModalOpen} discount={editingDiscount} onClose={closeSetModal} />
          {deleteDiscountId && (
            <Modal
              open={!!deleteDiscountId}
              setOpen={(val) => !val && closeDeleteModal()}
            >
              <DeleteDiscount id={deleteDiscountId} onClose={closeDeleteModal} />
            </Modal>
          )}
        </>
      }
    />
  );
}
